import { useCallback, useRef } from "react";
import { buildOutgoingMentions } from "../mentions/chatMentionUtils";
import { createClientMessageId } from "../shared/chatApi";

export default function useChatComposerSend({
  topicModeKey,
  clearSendingDraft,
  restoreSendingDraft,
  clearSentImages,
  maxText,
  text,
  selectedMentions,
  pendingImagesRef,
  readOnly,
  notifyBlocked,
  onBeforeSend,
  scope,
  eventId,
  groupThreadId,
  effectiveSenderType,
  resolveNotificationDeliveryMode,
  submitImagesMessage,
  submitTextMessage,
  clearPendingImages,
  setText,
  setSelectedMentions,
  setMentionState,
  pushError,
  focusTextarea,
  clearErrors,
}) {
  const failedTextSendRef = useRef(null);

  return useCallback(async () => {
    clearErrors();
    const trimmed = text.trim();
    const pendingFiles = pendingImagesRef.current.map((entry) => entry.file).filter(Boolean);
    const hasPendingImages = pendingFiles.length > 0;
    const hasText = Boolean(trimmed);

    if (!hasPendingImages && !hasText) {
      pushError("メッセージが空です。");
      return;
    }
    if (readOnly) {
      notifyBlocked();
      return;
    }
    if (hasText && trimmed.length > maxText) {
      pushError(`テキストは ${maxText} 文字以内で入力してください。`);
      return;
    }

    let pendingText = null;
    try {
      if (hasText && typeof onBeforeSend === "function") {
        const mentions = buildOutgoingMentions(selectedMentions, trimmed);
        const shouldSend = await onBeforeSend({
          type: "text",
          text: trimmed,
          mentions,
          scope,
          eventId,
          groupThreadId,
          senderType: effectiveSenderType,
        });
        if (shouldSend === false) return;
      }
      const notificationDeliveryMode = await resolveNotificationDeliveryMode();
      if (!notificationDeliveryMode) return;
      const textSendFingerprint = hasText
        ? JSON.stringify({ topicModeKey, text, selectedMentions, scope, eventId, groupThreadId, effectiveSenderType })
        : '';
      const clientMessageId = failedTextSendRef.current?.fingerprint === textSendFingerprint
        ? failedTextSendRef.current.clientMessageId
        : createClientMessageId();
      pendingText = hasText
        ? { value: text, mentions: selectedMentions, fingerprint: textSendFingerprint, clientMessageId }
        : null;
      if (pendingText) {
        if (clearSendingDraft) clearSendingDraft();
        else { setText(""); setSelectedMentions([]); setMentionState(null); }
      }
      if (hasPendingImages) {
        await submitImagesMessage(pendingFiles, { notificationDeliveryMode });
        if (clearSentImages) clearSentImages();
        else clearPendingImages();
      }
      if (hasText) {
        await submitTextMessage(trimmed, {
          notificationDeliveryMode,
          skipBeforeSend: true,
          clientMessageId,
        });
        failedTextSendRef.current = null;
      }
    } catch (err) {
      if (pendingText) {
        failedTextSendRef.current = pendingText;
        if (restoreSendingDraft) restoreSendingDraft(pendingText);
        else {
          setText((currentText) => currentText || pendingText.value);
          setSelectedMentions((currentMentions) => currentMentions.length ? currentMentions : pendingText.mentions);
        }
      }
      pushError(
        err?.message ||
          (hasPendingImages ? "画像の送信に失敗しました。" : "テキスト送信に失敗しました。"),
      );
    } finally {
      requestAnimationFrame(focusTextarea);
    }
  }, [
    clearErrors,
    clearSendingDraft,
    restoreSendingDraft,
    clearSentImages,
    clearPendingImages,
    effectiveSenderType,
    eventId,
    focusTextarea,
    groupThreadId,
    maxText,
    notifyBlocked,
    onBeforeSend,
    pendingImagesRef,
    pushError,
    readOnly,
    resolveNotificationDeliveryMode,
    scope,
    selectedMentions,
    setMentionState,
    setSelectedMentions,
    setText,
    submitImagesMessage,
    submitTextMessage,
    text,
    topicModeKey,
  ]);
}
